/**
 * Pure element-level diff between two IFC element detail payloads (WHA-3695).
 *
 * The model diff RPC tells us WHICH elements changed; when the user selects a
 * modified element the diff panel fetches both sides' full properties via the
 * existing get_ifc_element_properties RPC and diffs them here, in the client —
 * no extra server round-trip beyond the two detail fetches it would make anyway.
 *
 * Kept free of i18n / formatting so it is trivially unit-testable: it reports
 * structural changes (which property/material/attribute differs and the raw
 * before/after values); the component localizes labels and formats values.
 */

import type {
  IfcElementDetails,
  IfcElementProperty,
  IfcElementMaterialAssignment,
} from "@/stores/ifcModel/repository";

export type DiffStatus = "added" | "removed" | "changed";

/** A scalar leaf of a (possibly complex) property value, for display. */
export type JsonScalar = string | number | boolean;

/** One field of a complex-quantity value_json, compared old→new. `same` fields
 * are kept so the expanded view can show the whole layer, not just what moved. */
type JsonFieldStatus = DiffStatus | "same";

interface JsonFieldDiff {
  key: string;
  before: JsonScalar | null;
  after: JsonScalar | null;
  status: JsonFieldStatus;
}

export interface PropertyDiffRow {
  key: string;
  pset_name: string;
  property_name: string;
  before: IfcElementProperty | null;
  after: IfcElementProperty | null;
  status: DiffStatus;
}

interface MaterialDiffRow {
  key: string;
  /** Material name; layer index (when present) disambiguates layer-set rows. */
  name: string;
  layer_index: number | null;
  before: IfcElementMaterialAssignment | null;
  after: IfcElementMaterialAssignment | null;
  status: DiffStatus;
}

export type AttributeField = "type" | "name" | "long_name" | "elevation" | "storey" | "parent";

interface AttributeDiffRow {
  field: AttributeField;
  before: string | null;
  after: string | null;
}

export interface ElementDiff {
  properties: PropertyDiffRow[];
  materials: MaterialDiffRow[];
  attributes: AttributeDiffRow[];
}

const NUL = "\u0000";

// ifcopenshell's get_info() stamps complex-quantity value_json (wall/slab layers)
// with the entity `id` (STEP line number) + `type`/`class`. Those are per-export
// surrogates — the id changes on EVERY re-export even when the real value is
// identical — so they make a layer look "changed" on every revision and bloat
// the rendered blob. Strip them before comparing / displaying. The pipeline now
// scrubs them at extraction too; this keeps already-stored data correct. (WHA-3695)
const ENTITY_META_KEYS = new Set(["id", "type", "class"]);

function stripEntityKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripEntityKeys);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (ENTITY_META_KEYS.has(k)) continue;
      out[k] = stripEntityKeys(v);
    }
    return out;
  }
  return value;
}

/** Canonical text of a value_json with surrogate keys removed — for equality. */
function canonicalJson(value_json: unknown): string {
  return JSON.stringify(stripEntityKeys(value_json) ?? null);
}

/**
 * Flatten a complex-quantity value_json into display entries: strip the
 * ifcopenshell surrogate keys, lift the inner `properties` map to top level, and
 * keep scalar siblings (e.g. `Discrimination`). Non-object values yield a single
 * unnamed entry. Used to render — and diff — wall/slab layer quantities legibly
 * instead of a raw JSON blob (WHA-3695).
 */
export function normalizeJsonValue(value_json: unknown): { key: string; value: JsonScalar }[] {
  const stripped = stripEntityKeys(value_json);
  if (stripped == null) return [];
  if (typeof stripped !== "object") {
    return [{ key: "", value: stripped as JsonScalar }];
  }
  const entries: { key: string; value: JsonScalar }[] = [];
  const pushScalar = (key: string, v: unknown) => {
    if (v == null) return;
    entries.push({ key, value: typeof v === "object" ? JSON.stringify(v) : (v as JsonScalar) });
  };
  for (const [k, v] of Object.entries(stripped as Record<string, unknown>)) {
    if (k === "properties" && v && typeof v === "object" && !Array.isArray(v)) {
      for (const [pk, pv] of Object.entries(v as Record<string, unknown>)) pushScalar(pk, pv);
    } else {
      pushScalar(k, v);
    }
  }
  return entries;
}

/**
 * Field-level diff of two complex-quantity value_json values. Returns every
 * field in the union (including unchanged ones, flagged `same`) so the UI can
 * show a compact summary of what moved AND, on expand, the full layer.
 */
export function diffJsonValues(beforeJson: unknown, afterJson: unknown): JsonFieldDiff[] {
  const b = new Map(normalizeJsonValue(beforeJson).map((e) => [e.key, e.value]));
  const a = new Map(normalizeJsonValue(afterJson).map((e) => [e.key, e.value]));
  const rows: JsonFieldDiff[] = [];
  for (const key of new Set([...b.keys(), ...a.keys()])) {
    const before = b.has(key) ? (b.get(key) as JsonScalar) : null;
    const after = a.has(key) ? (a.get(key) as JsonScalar) : null;
    const status: JsonFieldStatus =
      before == null ? "added" : after == null ? "removed" : before === after ? "same" : "changed";
    rows.push({ key, before, after, status });
  }
  rows.sort((x, y) => x.key.localeCompare(y.key));
  return rows;
}

/** Stable identity of one property (which Pset/Qto + property it is). */
function propKey(p: IfcElementProperty): string {
  return `${p.pset_name}${NUL}${p.property_name}`;
}

/** True when two property rows carry the same value across all four typed
 * columns. value_json is compared after stripping ifcopenshell surrogate keys,
 * so a layer that differs only by its per-export STEP id reads as unchanged. */
function sameValue(a: IfcElementProperty, b: IfcElementProperty): boolean {
  return (
    a.value_text === b.value_text &&
    a.value_number === b.value_number &&
    a.value_bool === b.value_bool &&
    canonicalJson(a.value_json) === canonicalJson(b.value_json)
  );
}

function materialKey(m: IfcElementMaterialAssignment): string {
  return `${m.name}${NUL}${m.layer_index ?? ""}`;
}

function sameMaterial(a: IfcElementMaterialAssignment, b: IfcElementMaterialAssignment): boolean {
  return (
    a.relation_kind === b.relation_kind &&
    a.layer_thickness === b.layer_thickness &&
    a.description === b.description
  );
}

function scalar(v: string | number | null | undefined): string | null {
  if (v == null) return null;
  return String(v);
}

/**
 * Diff `base` (older) against `target` (newer). Either side may be null (the
 * element is added or removed wholesale) — then every property/material is
 * reported added/removed accordingly. Only actual differences are returned;
 * identical properties/materials/attributes are omitted.
 */
export function diffElementProperties(
  base: IfcElementDetails | null,
  target: IfcElementDetails | null
): ElementDiff {
  // ---- properties ----------------------------------------------------------
  const baseProps = new Map((base?.properties ?? []).map((p) => [propKey(p), p]));
  const targetProps = new Map((target?.properties ?? []).map((p) => [propKey(p), p]));
  const properties: PropertyDiffRow[] = [];
  const propKeys = new Set([...baseProps.keys(), ...targetProps.keys()]);
  for (const key of propKeys) {
    const before = baseProps.get(key) ?? null;
    const after = targetProps.get(key) ?? null;
    const ref = after ?? before;
    if (!ref) continue; // unreachable — key came from one of the two maps
    if (before && after) {
      if (sameValue(before, after)) continue;
      properties.push({
        key,
        pset_name: ref.pset_name,
        property_name: ref.property_name,
        before,
        after,
        status: "changed",
      });
    } else {
      properties.push({
        key,
        pset_name: ref.pset_name,
        property_name: ref.property_name,
        before,
        after,
        status: after ? "added" : "removed",
      });
    }
  }
  properties.sort(
    (a, b) =>
      a.pset_name.localeCompare(b.pset_name) || a.property_name.localeCompare(b.property_name)
  );

  // ---- materials -----------------------------------------------------------
  const baseMats = new Map((base?.materials ?? []).map((m) => [materialKey(m), m]));
  const targetMats = new Map((target?.materials ?? []).map((m) => [materialKey(m), m]));
  const materials: MaterialDiffRow[] = [];
  const matKeys = new Set([...baseMats.keys(), ...targetMats.keys()]);
  for (const key of matKeys) {
    const before = baseMats.get(key) ?? null;
    const after = targetMats.get(key) ?? null;
    const ref = after ?? before;
    if (!ref) continue; // unreachable — key came from one of the two maps
    if (before && after) {
      if (sameMaterial(before, after)) continue;
      materials.push({
        key,
        name: ref.name,
        layer_index: ref.layer_index,
        before,
        after,
        status: "changed",
      });
    } else {
      materials.push({
        key,
        name: ref.name,
        layer_index: ref.layer_index,
        before,
        after,
        status: after ? "added" : "removed",
      });
    }
  }
  materials.sort(
    (a, b) => a.name.localeCompare(b.name) || (a.layer_index ?? 0) - (b.layer_index ?? 0)
  );

  // ---- base attributes -----------------------------------------------------
  const attributes: AttributeDiffRow[] = [];
  const be = base?.element;
  const te = target?.element;
  const pushAttr = (field: AttributeField, before: string | null, after: string | null) => {
    if (before !== after) attributes.push({ field, before, after });
  };
  pushAttr("type", scalar(be?.type), scalar(te?.type));
  pushAttr("name", scalar(be?.name), scalar(te?.name));
  pushAttr("long_name", scalar(be?.long_name), scalar(te?.long_name));
  pushAttr("elevation", scalar(be?.elevation), scalar(te?.elevation));
  // Containment: the diff RPC flags storey/parent re-hosting via attr_changed,
  // so the detail must render those rows too (else a re-hosted element shows an
  // empty diff). Raw GUIDs here; the component maps them to storey names.
  pushAttr("storey", scalar(be?.storey_guid), scalar(te?.storey_guid));
  pushAttr("parent", scalar(be?.parent_guid), scalar(te?.parent_guid));

  return { properties, materials, attributes };
}
